import { TOOLTIP_PORTAL_ID } from '@/consts/portal.consts';
import { AuthErrorScreen } from '@/features/common/components/AuthErrorScreen';
import { LoadingScreen } from '@/features/common/components/LoadingScreen';
import SetupAxios from '@/hocs/SetupAxios';
import { PostHogIdentify } from '@/hocs/PostHogIdentify';
import { Auth0User } from '@/models/user.model';
import {
  AppState,
  Auth0ContextInterface,
  Auth0Provider,
  Auth0ProviderOptions,
  useAuth0
} from '@auth0/auth0-react';
import { Context, PropsWithChildren } from 'react';
import { Outlet, ScrollRestoration } from 'react-router-dom';

const domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

export default function RootWrapper() {
  return (
    <Auth0ProviderWithRedirectCallback
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        scope: 'profile email openid offline_access',
        audience: audience,
        prompt: 'login'
      }}
    >
      <SetupAxios>
        <PostHogIdentify />
        <ScrollRestoration />
        <Root />

        {/* Portals */}
        <div id={TOOLTIP_PORTAL_ID} />
      </SetupAxios>
    </Auth0ProviderWithRedirectCallback>
  );
}

// Handles Auth0 Loading and Error
const Root = () => {
  const { isLoading, error } = useAuth0();

  return isLoading ? (
    <LoadingScreen />
  ) : error ? (
    <AuthErrorScreen error={(error as any).error} />
  ) : (
    <Outlet />
  );
};

export const Auth0ProviderWithRedirectCallback = ({
  children,
  context,
  ...props
}: PropsWithChildren<Omit<Auth0ProviderOptions, 'context'>> & {
  context?: Context<Auth0ContextInterface<Auth0User>>;
}) => {
  const onRedirectCallback = (appState?: AppState) => {
    window.location.replace(
      window.location.origin + (appState?.returnTo || window.location.pathname)
    );
  };

  return (
    <Auth0Provider
      onRedirectCallback={onRedirectCallback}
      context={context}
      {...props}
    >
      {children}
    </Auth0Provider>
  );
};
